import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useDmEvents } from '../../../../hooks/useDmEvents';
import type { DmEventItem } from '../../../../services/dmEvents';
import { groupByDate } from '../../../../utils/dateGrouping';
import { EventRow } from '../EventRow';
import { Card } from '../shared';

interface DmEventsCardProps {
    patientId: string;
    dmId: number | null | undefined;
    /** Stretch to the parent's height (e.g. Manage Tx) instead of the overview's 420px cap. */
    fill?: boolean;
}

const ACTIVE_PILL_STYLES: Record<string, string> = {
    all: 'bg-gray-800 text-white',
    alert: 'bg-red-500 text-white',
    warning: 'bg-amber-500 text-white',
    info: 'bg-blue-500 text-white',
    'goal-achieved': 'bg-emerald-500 text-white'
};

const FILTER_OPTIONS = [
    { key: 'all', label: 'All' },
    { key: 'alert', label: 'Critical' },
    { key: 'warning', label: 'Warning' },
    { key: 'info', label: 'Info' },
    { key: 'goal-achieved', label: 'Goals' }
] as const;

function matchesFilter(event: DmEventItem, filter: string): boolean {
    if (filter === 'all') return true;
    return event.level === filter;
}

function DmEventsCard({ patientId, dmId, fill = false }: DmEventsCardProps) {
    const sizing = fill ? { className: 'h-full' } : { maxHeight: 'max-h-[420px]' };
    const [activeFilter, setActiveFilter] = useState('all');
    const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useDmEvents(
        patientId,
        dmId
    );

    const filtered = useMemo(
        () => items.filter((e) => matchesFilter(e, activeFilter)),
        [items, activeFilter]
    );

    const groups = useMemo(() => groupByDate(filtered), [filtered]);

    if (!dmId) {
        return (
            <Card title="DM Events" {...sizing}>
                <div className="flex items-center justify-center h-24 text-xs text-gray-400">
                    DM not configured for this patient
                </div>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card title="DM Events" {...sizing}>
                <div className="flex flex-col gap-3">
                    <div className="h-3 w-12 bg-gray-100 animate-pulse rounded" />
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex items-start gap-3">
                            <div className="w-2 h-2 bg-gray-200 animate-pulse rounded-full mt-1.5 flex-shrink-0" />
                            <div className="flex-1 flex flex-col gap-1.5 bg-gray-50 rounded-lg p-3">
                                <div className="w-32 h-4 bg-gray-100 animate-pulse rounded" />
                                <div className="w-full h-3 bg-gray-100 animate-pulse rounded" />
                                <div className="w-16 h-3 bg-gray-100 animate-pulse rounded" />
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card title="DM Events" {...sizing}>
                <div className="flex items-center justify-center h-24 text-xs text-gray-400">
                    DM data unavailable
                </div>
            </Card>
        );
    }

    if (items.length === 0) {
        return (
            <Card title="DM Events" {...sizing}>
                <div className="flex items-center justify-center h-24 text-xs text-gray-400">
                    No DM events
                </div>
            </Card>
        );
    }

    return (
        <Card title="DM Events" {...sizing}>
            {/* Filter pills */}
            <div className="flex items-center gap-1.5 pb-3 overflow-x-auto scrollbar-hide -mx-1 px-1 flex-shrink-0">
                {FILTER_OPTIONS.map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setActiveFilter(key)}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-full whitespace-nowrap transition-colors ${
                            activeFilter === key
                                ? (ACTIVE_PILL_STYLES[key] ?? 'bg-gray-800 text-white')
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="overflow-y-auto scrollbar-hide min-h-0 flex-1 -mr-2 pr-2">
                {filtered.length === 0 ? (
                    <div className="flex items-center justify-center h-20 text-xs text-gray-400">
                        No events for this filter
                    </div>
                ) : (
                    groups.map((group) => (
                        <div key={group.label}>
                            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider py-2">
                                {group.label}
                            </div>
                            {group.items.map((event) => (
                                <EventRow key={event.eventId} event={event} />
                            ))}
                        </div>
                    ))
                )}

                {hasNextPage && filtered.length > 0 && (
                    <button
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 mt-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {isFetchingNextPage ? (
                            <span className="inline-flex items-center gap-1.5">
                                <span className="h-3 w-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                                Loading...
                            </span>
                        ) : (
                            <>
                                Load More
                                <ChevronDownIcon className="w-3.5 h-3.5" />
                            </>
                        )}
                    </button>
                )}
            </div>
        </Card>
    );
}

export { DmEventsCard };
